import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Bell, CheckCircle, AlertCircle, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import lanerunnerLogoWhite from '@/assets/lanerunner-logo-white.png';
import lanerunnerLogoBlack from '@/assets/lanerunner-logo-black.png';
import { useTheme } from 'next-themes';

const OptInPage = () => {
  const { theme } = useTheme();
  usePageMeta({
    title: 'SMS Opt-In',
    description: 'Subscribe to LANERUNNER SMS notifications. Receive load alerts, rate updates, and important freight updates directly to your phone.'
  });
  
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const phone = formData.get('phone') as string;
    const promotional = formData.get('promotional') === 'on';

    try {
      const response = await fetch('https://api.lanerunner.com/sms/opt-in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone,
          promotional_opt_in: promotional,
        }),
      });

      if (!response.ok) {
        throw new Error('Verification failed');
      }

      toast({
        title: 'Success',
        description: 'Your number was verified successfully.',
      });

      // Reset form
      e.currentTarget.reset();
    } catch (err) {
      setError('Verification failed. Try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <Link to="/">
            <img src={theme === 'dark' ? lanerunnerLogoWhite : lanerunnerLogoBlack} alt="LANERUNNER" className="h-8" />
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Secure Your Account
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Enable SMS verification to protect your account with two-factor authentication
          </p>
        </div>

        {/* Benefits Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-foreground mb-6">Why SMS Verification?</h2>
          
          <Card>
            <CardContent className="pt-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Two-factor authentication codes for secure login</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Security alerts for unusual account activity</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Password reset verification codes</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Critical account security notifications</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Form Section */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">Mobile Phone Number</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    required
                    className="text-base"
                  />
                </div>

                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="security" 
                      name="security" 
                      required 
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label 
                        htmlFor="security" 
                        className="text-sm font-normal cursor-pointer leading-relaxed"
                      >
                        I consent to receive automated text messages from LaneRunner for account security purposes, including two-factor authentication codes, security alerts, and password reset verification. These messages are required to protect my account.
                      </Label>
                    </div>
                  </div>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    Message and data rates may apply. Reply STOP to opt out, or HELP for help. By providing your mobile number, you agree to receive security verification codes via SMS. View our{' '}
                    <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
                  </AlertDescription>
                </Alert>

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Verifying...' : 'Verify My Number'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Important Information Section */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle>Important Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-foreground mb-2">Message Frequency:</h3>
              <p className="text-muted-foreground">
                You will receive SMS messages only when logging in, resetting your password, or when suspicious activity is detected on your account.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">Carrier Support:</h3>
              <p className="text-muted-foreground">
                Available on major carriers including AT&T, Verizon, T-Mobile, Sprint, US Cellular, Boost, MetroPCS, and others.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">Opting Out:</h3>
              <p className="text-muted-foreground">
                You can text STOP to opt out at any time. However, note that opting out will prevent you from receiving critical account security messages necessary to protect your account.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">Need help?</h3>
              <p className="text-muted-foreground">
                Contact{' '}
                <a href="mailto:support@lanerunner.com" className="text-primary hover:underline">
                  support@lanerunner.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default OptInPage;
